/** Local Home task preview — Workboard (scope-linked checklists). */

export const TASK_STATE_KEY = "taistu_home_task_preview_state";

const todayIso = () => new Date().toISOString().slice(0, 10);

const DEFAULT_SCOPES = [
  {
    id: "scope-brand",
    title: "Brand Identity System",
    tasks: [
      { id: "t-brand-1", title: "Lock wordmark direction", due: todayIso() },
      { id: "t-brand-2", title: "Type pairing options", due: todayIso() },
      { id: "t-brand-3", title: "Color system draft", due: offsetIso(2) },
    ],
  },
  {
    id: "scope-web",
    title: "Website Design + Development",
    tasks: [
      { id: "t-web-1", title: "Sitemap for shop path", due: todayIso() },
      { id: "t-web-2", title: "Homepage wireframe", due: offsetIso(3) },
      { id: "t-web-3", title: "CMS field map", due: offsetIso(5) },
    ],
  },
  {
    id: "scope-pack",
    title: "Packaging Templates",
    tasks: [
      { id: "t-pack-1", title: "SKU list from client", due: todayIso() },
      { id: "t-pack-2", title: "Die-line template v1", due: offsetIso(4) },
    ],
  },
];

const DEFAULT_ADMIN_TODAY = [
  {
    id: "a1",
    clientId: "demo-s7",
    clientName: "Studio 7 Miami",
    scopeId: "scope-brand",
    scopeTitle: "Brand Identity System",
    title: "Lock wordmark direction",
    due: todayIso(),
  },
  {
    id: "a2",
    clientId: "demo-s7",
    clientName: "Studio 7 Miami",
    scopeId: "scope-web",
    scopeTitle: "Website Design + Development",
    title: "Sitemap for shop path",
    due: todayIso(),
  },
  {
    id: "a3",
    clientId: "demo-harbor",
    clientName: "Harbor & Pine",
    scopeId: "scope-brand",
    scopeTitle: "Brand Identity System",
    title: "Send revised color board",
    due: todayIso(),
  },
  {
    id: "a4",
    clientId: "demo-field",
    clientName: "Field Theory Labs",
    scopeId: "scope-ops",
    scopeTitle: "Ops system setup",
    title: "Confirm Notion schema",
    due: todayIso(),
  },
];

function offsetIso(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function clientBucket(clientKey) {
  const all = readJson(TASK_STATE_KEY, {});
  return all[clientKey] || { done: {}, selectedScopeId: null, extras: {}, dueOverrides: {} };
}

function writeClientBucket(clientKey, bucket) {
  const all = readJson(TASK_STATE_KEY, {});
  all[clientKey] = bucket;
  writeJson(TASK_STATE_KEY, all);
}

export function scopesFromDashboard(data) {
  const steps = data?.scope_progress?.steps;
  if (Array.isArray(steps) && steps.length > 0) {
    return steps.map((step, idx) => {
      const id = String(step.id || step.milestone_id || `step-${idx}`);
      const title = String(step.title || `Scope ${idx + 1}`);
      const existing = DEFAULT_SCOPES.find(
        (s) => s.title.toLowerCase() === title.toLowerCase(),
      );
      return {
        id,
        title,
        tasks: existing
          ? existing.tasks.map((t) => ({ ...t, id: `${id}-${t.id}` }))
          : [
              { id: `${id}-1`, title: `Draft ${title}`, due: todayIso() },
              { id: `${id}-2`, title: `Review ${title}`, due: offsetIso(1) },
              { id: `${id}-3`, title: `Ship ${title}`, due: offsetIso(4) },
            ],
      };
    });
  }
  const items = data?.scope_items;
  if (Array.isArray(items) && items.length > 0) {
    return items.map((item, idx) => {
      const title = String(item.title || item.number || `Scope ${idx + 1}`);
      const id = `item-${idx}-${title.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`;
      return {
        id,
        title,
        tasks: [
          { id: `${id}-1`, title: `Outline ${title}`, due: todayIso() },
          { id: `${id}-2`, title: `Build ${title}`, due: offsetIso(2) },
          { id: `${id}-3`, title: `Polish ${title}`, due: offsetIso(5) },
        ],
      };
    });
  }
  return DEFAULT_SCOPES.map((s) => ({
    ...s,
    tasks: s.tasks.map((t) => ({ ...t })),
  }));
}

export function loadWorkspacePreview(clientKey, data) {
  const scopes = scopesFromDashboard(data);
  const bucket = clientBucket(clientKey);
  const selectedScopeId =
    bucket.selectedScopeId && scopes.some((s) => s.id === bucket.selectedScopeId)
      ? bucket.selectedScopeId
      : scopes[0]?.id || null;
  return { scopes, selectedScopeId };
}

export function saveSelectedScope(clientKey, selectedScopeId) {
  const bucket = clientBucket(clientKey);
  writeClientBucket(clientKey, { ...bucket, selectedScopeId });
}

export function toggleWorkspaceTask(clientKey, taskId) {
  const bucket = clientBucket(clientKey);
  const done = { ...(bucket.done || {}) };
  done[taskId] = !done[taskId];
  writeClientBucket(clientKey, { ...bucket, done });
  return done;
}

export function addWorkspaceTask(clientKey, scopeId, { title, due }) {
  const label = String(title || "").trim();
  if (!label) return null;
  const bucket = clientBucket(clientKey);
  const extras = { ...(bucket.extras || {}) };
  const list = Array.isArray(extras[scopeId]) ? [...extras[scopeId]] : [];
  const id = `extra-${scopeId}-${Date.now()}`;
  list.push({ id, title: label, due: (due || todayIso()).slice(0, 10) });
  extras[scopeId] = list;
  writeClientBucket(clientKey, { ...bucket, extras });
  return { id, title: label, due: (due || todayIso()).slice(0, 10), done: false };
}

export function mergeExtras(clientKey, scopes) {
  const bucket = clientBucket(clientKey);
  const extras = bucket.extras || {};
  const done = bucket.done || {};
  return scopes.map((scope) => {
    const extraTasks = Array.isArray(extras[scope.id]) ? extras[scope.id] : [];
    return {
      ...scope,
      tasks: [
        ...scope.tasks,
        ...extraTasks,
      ].map((t) => ({
        ...t,
        due: (t.due || todayIso()).slice(0, 10),
        done: Boolean(done[t.id]),
      })),
    };
  });
}

export function overallProgressPct(scopes) {
  if (!scopes.length) return 0;
  let sum = 0;
  scopes.forEach((scope) => {
    const total = scope.tasks.length || 1;
    const doneCount = scope.tasks.filter((t) => t.done).length;
    sum += (doneCount / total) * 100;
  });
  return Math.round(sum / scopes.length);
}

export function scopeProgressPct(scope) {
  if (!scope?.tasks?.length) return 0;
  const doneCount = scope.tasks.filter((t) => t.done).length;
  return Math.round((doneCount / scope.tasks.length) * 100);
}

export function loadAdminTodayPreview() {
  const bucket = clientBucket("__admin_today__");
  const done = bucket.done || {};
  const extras = Array.isArray(bucket.adminExtras) ? bucket.adminExtras : [];
  const titleOverrides = bucket.titleOverrides || {};
  const dueOverrides = bucket.dueOverrides || {};
  const rows = [...DEFAULT_ADMIN_TODAY, ...extras].map((row) => ({
    ...row,
    title: titleOverrides[row.id] || row.title,
    due: (dueOverrides[row.id] || row.due || todayIso()).slice(0, 10),
    done: Boolean(done[row.id]),
  }));
  return rows.filter((row) => row.due === todayIso() || (!row.done && row.due === todayIso()));
}

/** Open tasks due today (incomplete + today's completed still shown until cleared — open only). */
export function loadAdminOpenToday() {
  const bucket = clientBucket("__admin_today__");
  const done = bucket.done || {};
  const extras = Array.isArray(bucket.adminExtras) ? bucket.adminExtras : [];
  const titleOverrides = bucket.titleOverrides || {};
  const dueOverrides = bucket.dueOverrides || {};
  return [...DEFAULT_ADMIN_TODAY, ...extras]
    .map((row) => ({
      ...row,
      title: titleOverrides[row.id] || row.title,
      due: (dueOverrides[row.id] || row.due || todayIso()).slice(0, 10),
      done: Boolean(done[row.id]),
    }))
    .filter((row) => row.due === todayIso() && !row.done);
}

export function toggleAdminTodayTask(taskId) {
  const bucket = clientBucket("__admin_today__");
  const done = { ...(bucket.done || {}) };
  done[taskId] = !done[taskId];
  writeClientBucket("__admin_today__", { ...bucket, done });
  return done;
}

export function addAdminTodayTask({ title, due, clientName, scopeTitle }) {
  const label = String(title || "").trim();
  if (!label) return null;
  const bucket = clientBucket("__admin_today__");
  const extras = Array.isArray(bucket.adminExtras) ? [...bucket.adminExtras] : [];
  const id = `admin-extra-${Date.now()}`;
  const row = {
    id,
    clientId: "demo-local",
    clientName: clientName || "Studio",
    scopeId: "scope-local",
    scopeTitle: scopeTitle || "Scope",
    title: label,
    due: (due || todayIso()).slice(0, 10),
  };
  extras.push(row);
  writeClientBucket("__admin_today__", { ...bucket, adminExtras: extras });
  return row;
}

/** Update title and/or due for an admin home task. */
export function updateAdminTodayTask(taskId, patch = {}) {
  if (!taskId) return null;
  const bucket = clientBucket("__admin_today__");
  const extras = Array.isArray(bucket.adminExtras) ? [...bucket.adminExtras] : [];
  const idx = extras.findIndex((r) => r.id === taskId);

  if (idx >= 0) {
    const next = { ...extras[idx] };
    if (patch.title != null) {
      const label = String(patch.title).trim();
      if (label) next.title = label;
    }
    if (patch.due != null) {
      const due = String(patch.due).slice(0, 10);
      if (due) next.due = due;
    }
    extras[idx] = next;
    writeClientBucket("__admin_today__", { ...bucket, adminExtras: extras });
    return next;
  }

  const titleOverrides = { ...(bucket.titleOverrides || {}) };
  const dueOverrides = { ...(bucket.dueOverrides || {}) };
  if (patch.title != null) {
    const label = String(patch.title).trim();
    if (label) titleOverrides[taskId] = label;
  }
  if (patch.due != null) {
    const due = String(patch.due).slice(0, 10);
    if (due) dueOverrides[taskId] = due;
  }
  writeClientBucket("__admin_today__", { ...bucket, titleOverrides, dueOverrides });
  return { id: taskId, ...patch };
}
